// countRecurrenceOccurrences — pure-frontend mirror of backend
// `recurrenceService.generateOccurrences` (minus the DynamoDB
// writes). Single source of truth for "how many occurrences will
// this rule generate over [startDate, endDate]?". Used by
// RecurrencePreviewLine so the on-screen count exactly matches what
// generation produces on Save.
//
// Backend reference: backend/src/services/recurrenceService.ts. The
// step semantics:
//   - daily / weekly: simple day arithmetic
//   - monthly / quarterly / annual: original anchor day preserved
//     across month-end clamps (Jan 31 + 1 month → Feb 28, then Mar 31
//     from the original anchor, not Mar 28 from the clamped value)
//   - inclusive at endDate (current > endDate exits the loop)
//   - 104-occurrence safety cap matches DEFAULT_MAX_OCCURRENCES on
//     the backend so an open-ended rule renders the same count the
//     server would generate

import type { RecurrenceFrequency, RecurrenceRule } from "../../../../types";

const DEFAULT_MAX_OCCURRENCES = 104;

interface ParsedDate {
  year: number;
  month: number;
  day: number;
}

function parseDate(iso: string): ParsedDate | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return { year, month, day };
}

function formatDate(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number): number {
  // month is 1-12. new Date(year, month, 0) returns the last day of
  // the previous (0-indexed) month — i.e. the last day of `month`.
  return new Date(year, month, 0).getDate();
}

function addDays(year: number, month: number, day: number, days: number): string {
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() + days);
  return formatDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

function addMonthsFromAnchor(
  year: number,
  month: number,
  anchorDay: number,
  months: number,
): string {
  const totalMonths = month - 1 + months;
  const newYear = year + Math.floor(totalMonths / 12);
  const newMonth = (totalMonths % 12) + 1;
  const clampedDay = Math.min(anchorDay, daysInMonth(newYear, newMonth));
  return formatDate(newYear, newMonth, clampedDay);
}

function stepFromStart(
  start: ParsedDate,
  anchorDay: number,
  frequency: RecurrenceFrequency,
  interval: number,
  stepIndex: number,
): string {
  switch (frequency) {
    case "daily":
      return addDays(start.year, start.month, anchorDay, interval * stepIndex);
    case "weekly":
      return addDays(
        start.year,
        start.month,
        anchorDay,
        interval * 7 * stepIndex,
      );
    case "monthly":
      return addMonthsFromAnchor(
        start.year,
        start.month,
        anchorDay,
        interval * stepIndex,
      );
    case "quarterly":
      return addMonthsFromAnchor(
        start.year,
        start.month,
        anchorDay,
        interval * 3 * stepIndex,
      );
    case "annually":
      return addMonthsFromAnchor(
        start.year,
        start.month,
        anchorDay,
        interval * 12 * stepIndex,
      );
    default:
      return formatDate(start.year, start.month, anchorDay);
  }
}

export function countRecurrenceOccurrences(
  rule: Pick<RecurrenceRule, "frequency" | "interval" | "startDate" | "endDate">,
  maxOccurrences: number = DEFAULT_MAX_OCCURRENCES,
): number {
  const start = parseDate(rule.startDate);
  if (!start) return 0;
  if (rule.endDate) {
    const end = parseDate(rule.endDate);
    if (!end) return 0;
    if (rule.endDate < rule.startDate) return 0;
  }

  const interval = rule.interval && rule.interval > 0 ? rule.interval : 1;
  const anchorDay = start.day;

  let count = 0;
  let current = rule.startDate;
  let stepIndex = 0;
  while (count < maxOccurrences) {
    if (rule.endDate && current > rule.endDate) break;
    count += 1;
    stepIndex += 1;
    const next = stepFromStart(
      start,
      anchorDay,
      rule.frequency,
      interval,
      stepIndex,
    );
    if (next <= current) break;
    current = next;
  }
  return count;
}
